import { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDate, DeadlineWarning, projectInfo, deadlineLabel, StatusBadge, FileChips, trFold } from "@/components/shared";
import { Scale, Trash2, Pencil, CalendarIcon, CalendarClock, Award, Users, FileSpreadsheet, Inbox, Clock, Files, BellRing, CheckCircle2, FilePlus, XCircle, Undo2, ShieldCheck, Search, X, Trophy, List, CalendarDays, HandCoins, ArrowDownAZ } from "lucide-react";
import { TenderCalendar } from "@/components/TenderCalendar";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";
import { useAuth } from "@/context/AuthContext";
import { hasPrivilege, getPrivilegeToken } from "@/lib/privilege";

const KPI = ({ icon: Icon, label, value, tid, accent, onClick, active }) => {
  const len = String(value ?? "").length;
  const sizeCls = len > 12 ? "text-lg" : len > 9 ? "text-xl" : len > 6 ? "text-2xl" : "text-3xl";
  return (
    <div
      onClick={onClick}
      data-testid={tid}
      className={`border bg-white p-4 flex items-start justify-between gap-2 min-w-0 ${onClick ? "cursor-pointer hover:border-slate-500 transition-colors duration-150" : ""} ${active ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200"}`}
      title={onClick ? "Filtrelemek için tıklayın" : undefined}
    >
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 break-words">{label}</div>
        <div className={`font-heading ${sizeCls} font-extrabold tracking-tighter mt-1.5 break-all ${accent || ""}`}>{value}</div>
      </div>
      <div className="bg-slate-100 p-1.5 text-slate-600 shrink-0">
        <Icon size={13} />
      </div>
    </div>
  );
};

export default function RequestsPage() {
  const { user } = useAuth();
  const isCustomer = user?.role === "customer";
  const base = isCustomer ? "/musteri/teklifler" : "/admin/teklifler";
  const [requests, setRequests] = useState([]);
  const [rates, setRates] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [toEdit, setToEdit] = useState(null);
  const [preview, setPreview] = useState(null);
  const [expandedSup, setExpandedSup] = useState({});
  const [decision, setDecision] = useState(null);
  const [decisionNote, setDecisionNote] = useState("");
  const [decisionPw, setDecisionPw] = useState("");
  const [delPw, setDelPw] = useState("");
  const [deciding, setDeciding] = useState(false);
  const [editMessage, setEditMessage] = useState("");
  const [editTitle, setEditTitle] = useState("");
  const [editZone, setEditZone] = useState("");
  const [editStand, setEditStand] = useState("");
  const [editType, setEditType] = useState("");
  const [editArea, setEditArea] = useState("");
  const [editDeadline, setEditDeadline] = useState(null);
  const [editTime, setEditTime] = useState("18:00");
  const [saving, setSaving] = useState(false);
  const [toExtend, setToExtend] = useState(null);
  const [extDate, setExtDate] = useState(null);
  const [extTime, setExtTime] = useState("18:00");
  const [extReason, setExtReason] = useState("");
  const [extReasonPreset, setExtReasonPreset] = useState("none");
  const [extending, setExtending] = useState(false);
  const [extSuppliers, setExtSuppliers] = useState([]);
  const [extSel, setExtSel] = useState([]);
  const [extQ, setExtQ] = useState("");
  const trNow = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Istanbul" }));
  const extIsToday = extDate && format(extDate, "yyyy-MM-dd") === format(trNow, "yyyy-MM-dd");
  const extTimeOpts = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`)
    .filter((t) => !extIsToday || parseInt(t, 10) > trNow.getHours());
  useEffect(() => {
    if (extTimeOpts.length && !extTimeOpts.includes(extTime)) setExtTime(extTimeOpts[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extDate]);
  const [tab, setTabRaw] = useState("all");
  const [kpiFilter, setKpiFilter] = useState(null);
  const [sortMode, setSortMode] = useState("newest");
  const setTab = (t) => { setTabRaw(t); setKpiFilter(null); };
  const [search, setSearch] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const visibleIdsRef = useRef(null);
  const [viewMode, setViewMode] = useState("list");

  const loadRequests = () => api.get("/requests").then((r) => setRequests(r.data)).catch(() => {}).finally(() => setLoaded(true));

  useEffect(() => {
    loadRequests();
    api.get("/rates").then((r) => setRates(r.data)).catch(() => {});
  }, []);

  const canUndo = (request, offer) =>
    offer.status === "rejected" &&
    offer.decided_at &&
    Date.now() - new Date(offer.decided_at).getTime() < 24 * 3600 * 1000 &&
    !(request.offers_summary || []).some((o) => o.status === "accepted");

  const undoDecision = async () => {
    try {
      await api.post(`/offers/${preview.offer.id}/undo-reject`);
      toast.success("Karar geri alındı, teklif yeniden değerlendirmede");
      setPreview(null);
      loadRequests();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitDecision = async () => {
    const priv = hasPrivilege();
    if (decision.type === "accept" && !isCustomer && !priv && !decisionPw) return toast.error("Kabul işlemi için süper şifre gerekli");
    setDeciding(true);
    try {
      const payload = { note: decisionNote };
      if (decision.type === "accept" && !isCustomer) {
        if (priv) payload.privilege_token = getPrivilegeToken();
        else payload.super_password = decisionPw;
      }
      await api.post(`/offers/${decision.offer.id}/${decision.type}`, payload);
      toast.success(decision.type === "accept" ? "Teklif kabul edildi" : "Teklif reddedildi");
      setDecision(null);
      setDecisionNote("");
      setDecisionPw("");
      setPreview(null);
      loadRequests();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeciding(false);
    }
  };

  const toTRY = useMemo(
    () => (o) => (o.currency === "TRY" ? o.price : rates?.[o.currency] ? o.price * rates[o.currency] : null),
    [rates]
  );

  const bestOffer = (r) => {
    const offers = r.offers_summary || [];
    if (offers.length === 0) return null;
    if (offers.every((o) => toTRY(o) !== null)) return offers.reduce((a, b) => (toTRY(a) <= toTRY(b) ? a : b), offers[0]);
    if (offers.every((o) => o.currency === offers[0].currency)) return offers.reduce((a, b) => (a.price <= b.price ? a : b), offers[0]);
    return null;
  };

  const openEdit = (r) => {
    setToEdit(r);
    setEditTitle(r.title || "");
    setEditMessage(r.message);
    setEditDeadline(parseISO(r.deadline));
    setEditTime(r.deadline_time || "18:00");
    setEditZone(r.project?.zone || "");
    setEditStand(r.project?.stand_no || "");
    setEditType(r.project?.project_type || "");
    setEditArea(r.project?.area_m2 != null ? String(r.project.area_m2) : "");
  };

  const openExtend = (r) => {
    setToExtend(r);
    setExtDate(null);
    setExtTime(r.deadline_time || "18:00");
    setExtReason("");
    setExtReasonPreset("none");
    setExtSel([]);
    setExtQ("");
    setExtSuppliers([]);
    const invited = new Set((r.suppliers || []).map((s) => s.id));
    const bidders = new Set((r.offers_summary || []).map((o) => o.supplier_id));
    api.get("/users").then((res) =>
      setExtSuppliers((res.data || [])
        .filter((u) => u.role === "supplier" && u.is_active !== false && (!invited.has(u.id) || !bidders.has(u.id)))
        .map((u) => ({ ...u, _invitedNoBid: invited.has(u.id) && !bidders.has(u.id) })))
    ).catch(() => {});
  };

  const quickPick = (days) => {
    const cur = parseISO(toExtend.deadline);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const from = cur > today ? cur : today;
    const d = new Date(from);
    d.setDate(d.getDate() + days);
    setExtDate(d);
  };

  const submitExtend = async () => {
    if (!extDate) return toast.error("Yeni son tarih seçin");
    setExtending(true);
    try {
      const r = await api.post(`/requests/${toExtend.id}/extend-deadline`, {
        deadline: format(extDate, "yyyy-MM-dd"),
        deadline_time: extTime,
        reason: [extReasonPreset !== "none" ? extReasonPreset : null, extReason.trim() || null].filter(Boolean).join("\n"),
        extra_supplier_ids: extSel,
      });
      const inv = r.data?.invited_count || 0;
      toast.success(`İhale süresi uzatıldı${inv ? `, ${inv} yeni firma davet edildi` : ""} — tedarikçiler e-posta ile bilgilendirildi`);
      setToExtend(null);
      loadRequests();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExtending(false);
    }
  };

  const saveEdit = async () => {
    if (!editTitle.trim()) return toast.error("Başlık boş olamaz");
    if (!editMessage.trim()) return toast.error("Talep metni boş olamaz");
    if (!editDeadline) return toast.error("Son tarih seçin");
    setSaving(true);
    try {
      const payload = { title: editTitle.trim(), message: editMessage, deadline: format(editDeadline, "yyyy-MM-dd"), deadline_time: editTime };
      await api.patch(`/requests/${toEdit.id}`, payload);
      if (toEdit.project?.id) {
        const pPayload = {};
        if (editZone && editZone !== toEdit.project.zone) pPayload.zone = editZone;
        if (editStand.trim() && editStand.trim() !== toEdit.project.stand_no) pPayload.stand_no = editStand.trim();
        if (editType && editType !== toEdit.project.project_type) pPayload.project_type = editType;
        const areaNum = editArea === "" ? null : parseFloat(String(editArea).replace(",", "."));
        if (areaNum != null && !isNaN(areaNum) && areaNum !== toEdit.project.area_m2) pPayload.area_m2 = areaNum;
        if (Object.keys(pPayload).length) await api.patch(`/projects/${toEdit.project.id}`, pPayload);
      }
      toast.success("Talep güncellendi, tedarikçiler bilgilendirildi");
      setToEdit(null);
      loadRequests();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const priv = hasPrivilege();
    if (!priv && !delPw) return toast.error("Silme işlemi için süper şifre gerekli");
    try {
      await api.delete(`/requests/${toDelete.id}`, { data: priv ? { privilege_token: getPrivilegeToken() } : { super_password: delPw } });
      setRequests((rs) => rs.filter((x) => x.id !== toDelete.id));
      toast.success("Teklif talebi silindi");
      setToDelete(null);
      setDelPw("");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-8 fade-in">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Takip</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1">{isCustomer ? "Tekliflerim" : "Tüm Teklifler"}</h1>
        </div>
        {!isCustomer && (
        <Button
          variant="outline"
          data-testid="export-excel-button"
          onClick={async () => {
            try {
              const ids = visibleIdsRef.current;
              const res = await api.get(`/requests/export${ids ? `?ids=${ids.join(",")}` : ""}`, { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = "gettproposal_teklifler.xlsx";
              a.click();
              URL.revokeObjectURL(url);
            } catch {
              toast.error("Excel dosyası oluşturulamadı");
            }
          }}
          className="rounded-none border-slate-200 hover:bg-slate-100 gap-2 transition-colors duration-200"
        >
          <FileSpreadsheet size={15} /> Excel'e Aktar
        </Button>
        )}
      </div>

      {requests.length === 0 ? (
        <div className="border border-slate-200 bg-white px-4 py-14 text-center text-slate-500" data-testid="requests-empty">
          {loaded ? "Henüz teklif talebi oluşturulmadı." : "Yükleniyor..."}
        </div>
      ) : (() => {
        const q = trFold(search.trim());
        const matches = (r) => {
          if (!q) return true;
          const hay = trFold([r.title, r.project?.name, r.project?.zone, r.project?.stand_no,
                       ...(r.suppliers || []).map((s) => s.company_name),
                       ...(r.offers_summary || []).map((o) => o.company_name)]
            .filter(Boolean).join(" "));
          return hay.includes(q);
        };
        const byDeadline = (r) => `${r.deadline} ${r.deadline_time || "23:59"}`;
        const hasWinner = (r) => (r.offers_summary || []).some((o) => o.status === "accepted");
        const allRejected = (r) => (r.offer_count || 0) === 0 || (r.offers_summary || []).every((o) => o.status === "rejected");
        const lastOfferAt = (r) => (r.offers_summary || []).reduce((m, o) => ((o.created_at || "") > m ? o.created_at : m), "");
        const searched = requests.filter(matches);
        const inApproval = (r) => (r.pending_approvers || []).length > 0;
        const tabPred = tab === "active" ? (r) => !hasWinner(r) && (!r.expired || allRejected(r))
          : tab === "expired" ? (r) => r.expired && !hasWinner(r) && !allRejected(r) && !inApproval(r)
          : tab === "won" ? hasWinner
          : tab === "approval" ? inApproval : () => true;
        const tabBase = searched.filter(tabPred);
        const zoneOf = (r) => r.project?.zone || "Diğer";
        const zones = [...new Set([...tabBase.map(zoneOf), ...(zoneFilter ? [zoneFilter] : [])])].sort((a, b) => a.localeCompare(b, "tr"));
        const zoneCount = (z) => tabBase.filter((r) => zoneOf(r) === z).length;
        const filtered = searched.filter((r) => !zoneFilter || zoneOf(r) === zoneFilter);
        const activeReqs = filtered.filter((r) => !hasWinner(r) && (!r.expired || allRejected(r))).sort((a, b) => {
          const la = lastOfferAt(a), lb = lastOfferAt(b);
          if (la || lb) return lb.localeCompare(la);
          return byDeadline(a).localeCompare(byDeadline(b));
        });
        const expiredReqs = filtered.filter((r) => r.expired && !hasWinner(r) && !allRejected(r) && !inApproval(r)).sort((a, b) => byDeadline(b).localeCompare(byDeadline(a)));
        const wonAt = (r) => (r.offers_summary || []).find((o) => o.status === "accepted")?.decided_at || "";
        const wonReqs = filtered.filter(hasWinner).sort((a, b) => wonAt(b).localeCompare(wonAt(a)));
        const allReqs = filtered.slice().sort((a, b) => byDeadline(b).localeCompare(byDeadline(a)));
        const approvalReqs = filtered.filter(inApproval).sort((a, b) => byDeadline(b).localeCompare(byDeadline(a)));
        const kpiPreds = {
          active: (r) => !hasWinner(r) && (!r.expired || allRejected(r)),
          expired: (r) => r.expired && !hasWinner(r) && !allRejected(r),
          won: hasWinner,
          hasOffers: (r) => (r.offer_count || 0) > 0,
          hasNew: (r) => (r.new_offers || 0) > 0,
          soon: (r) => { const end = new Date(`${r.deadline}T${r.deadline_time || "23:59"}:00`); const d = end - Date.now(); return d > 0 && d <= 24 * 3600 * 1000; },
          undecided: (r) => (r.offer_count || 0) > 0 && !hasWinner(r),
          noOffers: (r) => !(r.offer_count > 0),
        };
        const baseVisible = tab === "all" ? allReqs : tab === "active" ? activeReqs : tab === "won" ? wonReqs : tab === "approval" ? approvalReqs : expiredReqs;
        const kpiApplied = kpiFilter && kpiPreds[kpiFilter] ? baseVisible.filter(kpiPreds[kpiFilter]) : baseVisible;
        const visible = sortMode === "alpha"
          ? kpiApplied.slice().sort((a, b) => (a.title || "").localeCompare(b.title || "", "tr", { sensitivity: "base" }))
          : kpiApplied.slice().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        visibleIdsRef.current = (tab === "all" && !zoneFilter && !q && !kpiFilter) ? null : visible.map((r) => r.id);
        return (
        <>
        <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[260px]" data-testid="requests-search">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="İhale, proje veya tedarikçi ara..."
            data-testid="requests-search-input"
            className="rounded-none border-slate-200 h-10 pl-9 pr-9 bg-white"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              data-testid="requests-search-clear"
              title="Aramayı temizle"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900 transition-colors duration-200"
            >
              <X size={15} />
            </button>
          )}
        </div>
        <div className="flex border border-slate-200" data-testid="requests-sort-toggle">
          <button onClick={() => setSortMode("newest")} data-testid="sort-newest-button" title="En son eklenen en üstte" className={`px-3 h-10 flex items-center gap-1.5 text-xs font-semibold transition-colors duration-200 ${sortMode === "newest" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-100"}`}>
            <Clock size={14} /> En Yeni
          </button>
          <button onClick={() => setSortMode("alpha")} data-testid="sort-alpha-button" title="Alfabetik sırala (A-Z)" className={`px-3 h-10 flex items-center gap-1.5 text-xs font-semibold transition-colors duration-200 ${sortMode === "alpha" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-100"}`}>
            <ArrowDownAZ size={14} /> A-Z
          </button>
        </div>
        {tab === "active" && (
          <div className="flex border border-slate-200" data-testid="view-toggle">
            <button onClick={() => setViewMode("list")} data-testid="view-list-button" title="Liste görünümü" className={`px-3 h-10 flex items-center gap-1.5 text-xs font-semibold transition-colors duration-200 ${viewMode === "list" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-100"}`}>
              <List size={14} /> Liste
            </button>
            <button onClick={() => setViewMode("calendar")} data-testid="view-calendar-button" title="Takvim görünümü" className={`px-3 h-10 flex items-center gap-1.5 text-xs font-semibold transition-colors duration-200 ${viewMode === "calendar" ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:bg-slate-100"}`}>
              <CalendarDays size={14} /> Takvim
            </button>
          </div>
        )}
        </div>
        <div className="flex border-b border-slate-200 flex-wrap" data-testid="requests-tabs">
          <button
            onClick={() => setTab("all")}
            data-testid="tab-all-requests"
            className={`px-5 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors duration-200 ${tab === "all" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}
          >
            Tümü ({allReqs.length})
          </button>
          <button
            onClick={() => setTab("active")}
            data-testid="tab-active-requests"
            className={`px-5 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors duration-200 ${tab === "active" ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}
          >
            Aktif İhaleler ({activeReqs.length})
          </button>
          <button
            onClick={() => setTab("expired")}
            data-testid="tab-expired-requests"
            className={`px-5 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors duration-200 ${tab === "expired" ? "border-red-500 text-red-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}
          >
            İhale Sona Erenler ({expiredReqs.length})
          </button>
          <button
            onClick={() => setTab("approval")}
            data-testid="tab-approval-requests"
            className={`px-5 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors duration-200 flex items-center gap-1.5 ${tab === "approval" ? "border-violet-600 text-violet-700" : "border-transparent text-slate-400 hover:text-slate-600"}`}
          >
            <ShieldCheck size={13} /> Onayda ({approvalReqs.length})
          </button>
          <button
            onClick={() => setTab("won")}
            data-testid="tab-won-requests"
            className={`px-5 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors duration-200 flex items-center gap-1.5 ${tab === "won" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-400 hover:text-slate-600"}`}
          >
            <Trophy size={13} /> Kazananlar ({wonReqs.length})
          </button>
          {tab === "won" && wonReqs.length > 0 && (
            <button
              onClick={async () => {
                try {
                  const ids = visibleIdsRef.current;
                  const res = await api.get(`/winners/pdf${ids ? `?ids=${ids.join(",")}` : ""}`, { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "gettproposal_kazanan_teklifler.pdf";
                  a.click();
                  URL.revokeObjectURL(url);
                } catch {
                  toast.error("PDF raporu oluşturulamadı");
                }
              }}
              data-testid="winners-pdf-button"
              className="ml-auto mb-1 inline-flex items-center gap-1.5 border border-emerald-600/40 bg-[#edf6e3] text-emerald-800 hover:bg-[#e3f0d6] text-xs font-semibold px-3 py-1.5 transition-colors duration-200"
            >
              <FileSpreadsheet size={13} /> PDF Rapor
            </button>
          )}
        </div>
        {zones.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="zone-filter">
            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Bölge:</span>
            <button
              onClick={() => setZoneFilter("")}
              data-testid="zone-filter-all"
              className={`px-3 py-1.5 text-xs font-semibold border transition-colors duration-200 ${!zoneFilter ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-100"}`}
            >
              Tüm Bölgeler ({tabBase.length})
            </button>
            {zones.map((z) => (
              <button
                key={z}
                onClick={() => setZoneFilter(zoneFilter === z ? "" : z)}
                data-testid={`zone-filter-${z.toLocaleLowerCase("tr-TR").replace(/\s+/g, "-")}`}
                className={`px-3 py-1.5 text-xs font-semibold border transition-colors duration-200 ${zoneFilter === z ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-100"}`}
              >
                {z} ({zoneCount(z)})
              </button>
            ))}
          </div>
        )}
        {(() => {
          const sum = (list, f) => list.reduce((a, r) => a + (f(r) || 0), 0);
          const fmtTL = (n) => n.toLocaleString("tr-TR", { maximumFractionDigits: 0 });
          let cards = [];
          if (tab === "all") {
            cards = [
              { icon: Files, label: "Toplam İhale", value: allReqs.length, tid: "kpi-all-count" },
              { icon: Inbox, label: "Aktif", value: activeReqs.length, tid: "kpi-all-active", accent: "text-blue-600", filter: "active" },
              { icon: Clock, label: "Sona Eren", value: expiredReqs.length, tid: "kpi-all-expired", accent: expiredReqs.length > 0 ? "text-red-600" : "", filter: "expired" },
              { icon: Trophy, label: "Kazanılan", value: wonReqs.length, tid: "kpi-all-won", accent: "text-emerald-600", filter: "won" },
              { icon: Award, label: "Gelen Teklif", value: sum(allReqs, (r) => r.offer_count), tid: "kpi-all-offers", filter: "hasOffers" },
            ];
          } else if (tab === "active") {
            const offers = sum(activeReqs, (r) => r.offer_count);
            const asked = sum(activeReqs, (r) => r.supplier_count);
            const soon = activeReqs.filter((r) => {
              const end = new Date(`${r.deadline}T${r.deadline_time || "23:59"}:00`);
              const diff = end - Date.now();
              return diff > 0 && diff <= 24 * 3600 * 1000;
            }).length;
            cards = [
              { icon: Inbox, label: "Aktif İhale", value: activeReqs.length, tid: "kpi-active-count" },
              { icon: Award, label: "Gelen Teklif", value: offers, tid: "kpi-active-offers", filter: "hasOffers" },
              { icon: BellRing, label: "Yeni Teklif", value: sum(activeReqs, (r) => r.new_offers), tid: "kpi-active-new", accent: sum(activeReqs, (r) => r.new_offers) > 0 ? "text-red-600" : "", filter: "hasNew" },
              { icon: Users, label: "Davet Edilen", value: asked, tid: "kpi-active-invited" },
              { icon: CheckCircle2, label: "Yanıt Oranı", value: `%${asked ? Math.round((offers / asked) * 100) : 0}`, tid: "kpi-active-rate", accent: "text-emerald-600" },
              { icon: CalendarClock, label: "24 Saatte Bitecek", value: soon, tid: "kpi-active-soon", accent: soon > 0 ? "text-amber-600" : "", filter: "soon" },
            ];
          } else if (tab === "expired") {
            const offers = sum(expiredReqs, (r) => r.offer_count);
            const asked = sum(expiredReqs, (r) => r.supplier_count);
            const undecided = expiredReqs.filter((r) => (r.offer_count || 0) > 0 && !(r.offers_summary || []).some((o) => o.status === "accepted")).length;
            const noOffer = expiredReqs.filter((r) => !(r.offer_count > 0)).length;
            cards = [
              { icon: Clock, label: "Sona Eren İhale", value: expiredReqs.length, tid: "kpi-expired-count" },
              { icon: Award, label: "Gelen Teklif", value: offers, tid: "kpi-expired-offers", filter: "hasOffers" },
              { icon: CheckCircle2, label: "Yanıt Oranı", value: `%${asked ? Math.round((offers / asked) * 100) : 0}`, tid: "kpi-expired-rate", accent: "text-emerald-600" },
              { icon: Scale, label: "Karar Bekleyen", value: undecided, tid: "kpi-expired-undecided", accent: undecided > 0 ? "text-amber-600" : "", filter: "undecided" },
              { icon: XCircle, label: "Teklifsiz Kapanan", value: noOffer, tid: "kpi-expired-nooffer", accent: noOffer > 0 ? "text-red-600" : "", filter: "noOffers" },
            ];
          } else if (tab === "approval") {
            const pendingNames = new Set(approvalReqs.flatMap((r) => r.pending_approvers || []));
            const pendingFirms = new Set(approvalReqs.flatMap((r) => r.pending_approval_companies || []));
            cards = [
              { icon: ShieldCheck, label: "Onayda Bekleyen", value: approvalReqs.length, tid: "kpi-approval-count", accent: "text-violet-600" },
              { icon: Users, label: "Bekleyen Onaylayıcı", value: pendingNames.size, tid: "kpi-approval-approvers" },
              { icon: Award, label: "Onaydaki Firma", value: pendingFirms.size, tid: "kpi-approval-firms" },
              { icon: Files, label: "Gelen Teklif", value: sum(approvalReqs, (r) => r.offer_count), tid: "kpi-approval-offers" },
            ];
          } else {
            const winners = wonReqs.map((r) => (r.offers_summary || []).find((o) => o.status === "accepted")).filter(Boolean);
            const tls = winners.map((o) => toTRY(o)).filter((v) => v != null);
            const totalTl = tls.reduce((a, b) => a + b, 0);
            const offerAvg = wonReqs.length ? Math.round(sum(wonReqs, (r) => r.offer_count) / wonReqs.length * 10) / 10 : 0;
            cards = [
              { icon: Trophy, label: "Kazanan İhale", value: wonReqs.length, tid: "kpi-won-count", accent: "text-emerald-600" },
              { icon: Award, label: "Toplam Tutar (TL)", value: tls.length ? fmtTL(totalTl) : "-", tid: "kpi-won-total", accent: "text-emerald-700" },
              { icon: Scale, label: "Ortalama Tutar (TL)", value: tls.length ? fmtTL(totalTl / tls.length) : "-", tid: "kpi-won-avg" },
              { icon: Files, label: "Ort. Teklif / İhale", value: offerAvg, tid: "kpi-won-offer-avg" },
              { icon: Users, label: "Kazanan Firma", value: new Set(winners.map((o) => o.company_name)).size, tid: "kpi-won-companies" },
            ];
          }
          return (
            <>
            <div className={`grid grid-cols-2 sm:grid-cols-3 gap-3 ${cards.length === 5 ? "lg:grid-cols-5" : "lg:grid-cols-6"}`} data-testid={`kpi-cards-${tab}`}>
              {cards.map((c) => (
                <KPI
                  key={c.tid}
                  icon={c.icon}
                  label={c.label}
                  value={c.value}
                  tid={c.tid}
                  accent={c.accent}
                  onClick={c.filter ? () => setKpiFilter((f) => (f === c.filter ? null : c.filter)) : kpiFilter ? () => setKpiFilter(null) : undefined}
                  active={!!c.filter && kpiFilter === c.filter}
                />
              ))}
            </div>
            {kpiFilter && (
              <div className="flex items-center gap-2 mt-3" data-testid="kpi-filter-chip">
                <span className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs font-semibold px-3 py-1.5">
                  Filtre: {cards.find((c) => c.filter === kpiFilter)?.label || kpiFilter} ({visible.length})
                  <button onClick={() => setKpiFilter(null)} data-testid="kpi-filter-clear" title="Filtreyi temizle" className="hover:text-red-300 transition-colors duration-150">
                    <X size={13} />
                  </button>
                </span>
              </div>
            )}
            </>
          );
        })()}
        {tab === "active" && viewMode === "calendar" ? (
          <TenderCalendar requests={activeReqs} base={`${isCustomer ? "/musteri" : "/admin"}/teklifler`} />
        ) : visible.length === 0 ? (
        <div className="border border-slate-200 bg-white px-4 py-14 text-center text-slate-500" data-testid="tab-empty">
          {q ? "Aramanızla eşleşen ihale bulunamadı." : kpiFilter ? "Bu filtreyle eşleşen ihale yok." : tab === "all" ? "Henüz ihale yok." : tab === "active" ? "Aktif ihale bulunmuyor." : tab === "won" ? "Henüz kazananı belirlenen ihale yok." : tab === "approval" ? "Şu anda yönetici onayında bekleyen ihale yok." : "Sona ermiş ihale bulunmuyor."}
        </div>
        ) : (
        <div className="space-y-4">
          {visible.map((r) => {
            const best = bestOffer(r);
            const pct = r.supplier_count ? Math.round((r.offer_count / r.supplier_count) * 100) : 0;
            const shown = expandedSup[r.id] ? r.suppliers : r.suppliers.slice(0, 3);
            return (
              <div key={r.id} className="border border-slate-200 bg-white p-5 flex flex-col lg:flex-row lg:items-center gap-5" data-testid={`request-row-${r.id}`}>
                <div className="lg:w-[26%] min-w-0">
                  <div className="font-heading font-bold text-xl tracking-tight flex items-center gap-2 flex-wrap">
                    <span className="truncate max-w-full">{r.title}</span>
                    {tab === "all" && (() => {
                      const st = hasWinner(r)
                        ? ["Kazanıldı", "bg-emerald-100 text-emerald-800 border-emerald-300"]
                        : r.expired
                        ? ["Sona Erdi", "bg-red-50 text-red-600 border-red-200"]
                        : ["Aktif", "bg-blue-50 text-blue-700 border-blue-200"];
                      return (
                        <span className={`text-[10px] font-bold px-2 py-0.5 border shrink-0 uppercase tracking-wide ${st[1]}`} data-testid={`status-badge-${r.id}`}>
                          {st[0]}
                        </span>
                      );
                    })()}
                    {r.new_offers > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 leading-none rounded-full shrink-0" data-testid={`new-offers-badge-${r.id}`}>
                        {r.new_offers} yeni teklif
                      </span>
                    )}
                    {r.pending_proposals > 0 && (
                      <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 leading-none rounded-full shrink-0 inline-flex items-center gap-1" data-testid={`counter-offer-badge-${r.id}`} title="Tedarikçiden karşı teklif / kabul yanıtı bekliyor">
                        <HandCoins size={10} /> {r.pending_proposals} karşı teklif
                      </span>
                    )}
                    {(r.pending_approvers || []).length > 0 && (
                      <span className="bg-violet-100 text-violet-700 border border-violet-300 text-[10px] font-semibold px-2 py-0.5 shrink-0 inline-flex items-center gap-1" data-testid={`pending-approver-badge-${r.id}`} title="Onayı bekleyen yönetici — onaylayana kadar her 24 saatte hatırlatma e-postası gönderilir">
                        <ShieldCheck size={10} /> Onay bekleniyor: {r.pending_approvers.join(", ")}
                      </span>
                    )}
                  </div>
                  {(r.pending_approval_companies || []).length > 0 ? (
                    <div className="flex items-center gap-1.5 text-xs mt-2 flex-wrap" data-testid={`approval-firm-${r.id}`}>
                      <ShieldCheck size={12} className="text-violet-600 shrink-0" />
                      <span className="text-slate-500">Onaydaki Firma:</span>
                      <span className="font-bold text-violet-700">{r.pending_approval_companies.join(", ")}</span>
                    </div>
                  ) : (
                  <>
                  {r.project && <div className="text-xs text-slate-500 mt-0.5">{projectInfo(r.project)}</div>}
                  <div className="flex items-center gap-x-1.5 gap-y-1 text-xs text-slate-500 mt-2 flex-wrap" data-testid={`request-suppliers-${r.id}`}>
                    <Users size={12} className="shrink-0" />
                    {shown.map((s, si) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setPreview({ request: r, supplier: s, offer: (r.offers_summary || []).find((o) => o.supplier_id === s.id) || null })}
                        data-testid={`supplier-preview-${r.id}-${s.id}`}
                        className="underline decoration-dotted underline-offset-2 hover:text-slate-900 transition-colors duration-150 truncate max-w-[150px]"
                        title="Teklif önizle"
                      >
                        {s.company_name}{si < shown.length - 1 ? "," : ""}
                      </button>
                    ))}
                    {!expandedSup[r.id] && r.suppliers.length > 3 && (
                      <button
                        type="button"
                        onClick={() => setExpandedSup((e) => ({ ...e, [r.id]: true }))}
                        data-testid={`expand-suppliers-${r.id}`}
                        className="text-slate-400 hover:text-slate-900 transition-colors duration-150"
                      >
                        +{r.suppliers.length - 3}
                      </button>
                    )}
                  </div>
                  </>
                  )}
                </div>

                <div className="lg:w-[18%]">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Son Tarih</div>
                  <div className="text-sm font-medium mt-0.5">{deadlineLabel(r)}</div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {(r.offers_summary || []).some((o) => o.status === "accepted") ? (
                      <Badge variant="outline" className="rounded-none text-[10px] bg-[#edf6e3] text-emerald-700 border-[#cde8ae] shrink-0" data-testid={`concluded-badge-${r.id}`}>İhale Sonuçlandı</Badge>
                    ) : r.expired && allRejected(r) ? (
                      <Badge variant="outline" className="rounded-none text-[10px] bg-amber-50 text-amber-700 border-amber-300 shrink-0" data-testid={`rebid-ready-badge-${r.id}`} title="Tüm teklifler reddedildi, ihale yeniden teklif almaya açılabilir">Yeniden İhaleye Açılabilir</Badge>
                    ) : (
                      <DeadlineWarning deadline={r.deadline} time={r.deadline_time} />
                    )}
                    {r.extensions?.length > 0 && (
                      <span className="text-[10px] text-amber-600 font-medium" data-testid={`extended-badge-${r.id}`} title={`Bu ihale ${r.extensions.length} kez uzatıldı`}>
                        {r.extensions.length}× uzatıldı
                      </span>
                    )}
                  </div>
                </div>

                <div className="lg:w-[20%]">
                  <div className="flex items-baseline justify-between">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Gelen Teklif</div>
                    <div className="text-sm" data-testid={`offer-count-${r.id}`}>
                      <span className="font-heading font-bold text-lg">{r.offer_count}</span>
                      <span className="text-slate-400"> / {r.supplier_count}</span>
                    </div>
                  </div>
                  <div className="h-1.5 bg-slate-100 mt-1.5">
                    <div className={`h-full ${pct === 100 ? "bg-emerald-500" : "bg-slate-900"}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>

                <div className="lg:w-[20%]">
                  {(() => {
                    const winner = (r.offers_summary || []).find((o) => o.status === "accepted");
                    if (winner)
                      return (
                        <div className="bg-[#edf6e3] border border-[#cde8ae] px-3 py-2 inline-block" data-testid={`winner-offer-${r.id}`}>
                          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-900 font-bold"><Trophy size={11} /> Kazanan Teklif</div>
                          <div className="font-heading font-extrabold text-xl tracking-tight text-slate-900 mt-0.5">
                            {winner.price.toLocaleString("tr-TR")} <span className="text-xs font-medium text-slate-500">{winner.currency}</span>
                          </div>
                          <div className="text-xs text-slate-600 truncate">{winner.company_name}</div>
                        </div>
                      );
                    if (best)
                      return (
                        <div data-testid={`best-offer-${r.id}`}>
                          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-600 font-bold"><Award size={11} /> En İyi Teklif</div>
                          <div className="font-heading font-extrabold text-xl tracking-tight text-emerald-600 mt-0.5">
                            {best.price.toLocaleString("tr-TR")} <span className="text-xs font-medium text-slate-500">{best.currency}</span>
                          </div>
                          <div className="text-xs text-slate-500 truncate">{best.company_name}</div>
                        </div>
                      );
                    return (
                      <div className="text-sm text-slate-400" data-testid={`best-offer-${r.id}`}>
                        {r.offer_count > 0 ? "Farklı para birimleri" : "Henüz teklif yok"}
                      </div>
                    );
                  })()}
                </div>

                <div className="flex items-center gap-2 lg:ml-auto shrink-0">
                  {r.project_id && (
                    <Link to={`${isCustomer ? "/musteri" : "/admin"}/teklif-al?proje=${r.project_id}`} data-testid={`request-quote-${r.id}`}>
                      <Button variant="outline" size="sm" className={`rounded-none h-9 gap-2 font-semibold transition-colors duration-200 ${r.expired && !(r.offer_count > 0) ? "border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100" : "border-slate-200 hover:bg-slate-100"}`}>
                        {r.expired && !(r.offer_count > 0) ? <><Undo2 size={13} /> Yeniden İhaleye Aç</> : <><FilePlus size={13} /> Teklif Al</>}
                      </Button>
                    </Link>
                  )}
                  <Link to={`${base}/${r.id}`} data-testid={`compare-link-${r.id}`}>
                    <Button size="sm" className="rounded-none bg-slate-900 text-white hover:bg-slate-700 gap-2 h-9 font-semibold transition-colors duration-200">
                      <Scale size={13} /> Karşılaştır
                    </Button>
                  </Link>
                  {!isCustomer && (
                  <>
                  {r.expired && (r.offer_count || 0) > 0 && allRejected(r) && !(r.offers_summary || []).some((o) => o.status === "accepted") && (
                    <Button variant="outline" size="sm" onClick={() => openExtend(r)} data-testid={`rebid-open-${r.id}`} className="rounded-none h-9 gap-2 border-amber-400 text-amber-700 bg-amber-50 hover:bg-amber-100 font-semibold transition-colors duration-200" title="Yeni son tarih vererek ihaleyi yeniden teklif almaya aç">
                      <Undo2 size={13} /> Yeniden İhaleye Aç
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => openExtend(r)} data-testid={`extend-request-${r.id}`} className="rounded-none h-9 border-amber-400/60 text-amber-600 hover:bg-amber-50 transition-colors duration-200" title="İhale Süresini Uzat">
                    <CalendarClock size={13} />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`edit-request-${r.id}`} className="rounded-none h-9 border-slate-200 hover:bg-slate-100 transition-colors duration-200" title="Düzenle">
                    <Pencil size={13} />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setToDelete(r)} data-testid={`delete-request-${r.id}`} className="rounded-none h-9 border-red-500/30 text-red-500 hover:bg-red-500/10 transition-colors duration-200" title="Sil">
                    <Trash2 size={13} />
                  </Button>
                  </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        )}
        </>
        );
      })()}

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-lg" data-testid="offer-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight">{preview?.supplier?.company_name}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-4">
              <div className="text-xs text-slate-500">{preview.request.title}{preview.request.project ? ` — ${projectInfo(preview.request.project)}` : ""}</div>
              {preview.offer ? (
                <>
                  <div className="flex items-end justify-between border border-slate-200 bg-slate-50 p-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400">Teklif</div>
                      <div className="font-heading font-extrabold text-3xl tracking-tight mt-0.5" data-testid="preview-offer-price">
                        {preview.offer.price.toLocaleString("tr-TR")} <span className="text-sm font-medium text-slate-500">{preview.offer.currency}</span>
                      </div>
                    </div>
                    <StatusBadge status={preview.offer.status} />
                  </div>
                  {preview.offer.description && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Açıklama</div>
                      <div className="text-sm text-slate-600 whitespace-pre-wrap max-h-40 overflow-y-auto" data-testid="preview-offer-description">{preview.offer.description}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Dosyalar</div>
                    <FileChips files={preview.offer.files} />
                  </div>
                  <div className="text-xs text-slate-400">Teklif tarihi: {formatDate(preview.offer.created_at)}</div>
                  {preview.offer.status === "pending" && (
                    <div className="flex gap-2">
                      <Button onClick={() => { setDecisionNote(""); setDecision({ type: "accept", offer: preview.offer }); }} data-testid="preview-accept-offer" className="flex-1 rounded-none bg-emerald-600 hover:bg-emerald-700 text-white h-10 font-semibold gap-2 transition-colors duration-200">
                        <CheckCircle2 size={14} /> Kabul Et
                      </Button>
                      <Button variant="outline" onClick={() => { setDecisionNote(""); setDecision({ type: "reject", offer: preview.offer }); }} data-testid="preview-reject-offer" className="flex-1 rounded-none border-red-500/30 text-red-500 hover:bg-red-500/10 h-10 font-semibold gap-2 transition-colors duration-200">
                        <XCircle size={14} /> Reddet
                      </Button>
                    </div>
                  )}
                  {canUndo(preview.request, preview.offer) && (
                    <Button variant="outline" onClick={undoDecision} data-testid="preview-undo-decision" className="w-full rounded-none h-10 border-slate-200 hover:bg-slate-100 gap-2 font-semibold transition-colors duration-200">
                      <Undo2 size={14} /> Red Kararını Geri Al
                    </Button>
                  )}
                </>
              ) : (
                <div className="border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500" data-testid="preview-no-offer">
                  Bu tedarikçi henüz teklif vermedi.
                </div>
              )}
              <Link to={`${base}/${preview.request.id}`} className="block">
                <Button variant="outline" className="rounded-none w-full h-10 border-slate-200 hover:bg-slate-100 gap-2 font-semibold transition-colors duration-200" data-testid="preview-open-compare">
                  <Scale size={14} /> Karşılaştırma Sayfasını Aç
                </Button>
              </Link>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!decision} onOpenChange={(o) => { if (!o) setDecision(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight">
              {decision?.type === "accept" ? "Teklifi kabul et?" : "Teklifi reddet?"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {decision?.type === "accept" && (
              <p className="text-sm text-slate-500">Bu teklifi kabul ettiğinizde aynı talepteki diğer tüm teklifler otomatik reddedilir ve tedarikçilere e-posta gönderilir.</p>
            )}
            <div className="space-y-1.5">
              <Label>Not (opsiyonel)</Label>
              <Textarea value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} rows={3} placeholder="Tedarikçiye iletilecek not..." data-testid="decision-note-input" className="rounded-none border-slate-200" />
            </div>
            {decision?.type === "accept" && !isCustomer && !hasPrivilege() && (
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-amber-700"><ShieldCheck size={13} /> Süper Şifre</Label>
                <Input type="password" value={decisionPw} onChange={(e) => setDecisionPw(e.target.value)} placeholder="Teklif kabulü süper şifre gerektirir" data-testid="decision-super-password-input" className="rounded-none border-amber-300 h-10 bg-amber-50/50" />
              </div>
            )}
            {decision?.type === "accept" && !isCustomer && hasPrivilege() && (
              <p className="text-[11px] text-emerald-700 flex items-center gap-1.5" data-testid="decision-privilege-note"><ShieldCheck size={12} /> Özel Yetki aktif — süper şifre gerekmez.</p>
            )}
            <Button onClick={submitDecision} disabled={deciding || (decision?.type === "accept" && !isCustomer && !hasPrivilege() && !decisionPw)} data-testid="confirm-decision-button" className={`w-full rounded-none h-10 font-semibold text-white transition-colors duration-200 ${decision?.type === "accept" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-500 hover:bg-red-600"}`}>
              {deciding ? "İşleniyor..." : decision?.type === "accept" ? "Kabul Et" : "Reddet"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toEdit} onOpenChange={(o) => { if (!o) setToEdit(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight">Talebi Düzenle — {toEdit?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="space-y-1.5">
              <Label>İhale Adı (Başlık)</Label>
              <Input data-testid="edit-title-input" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="rounded-none bg-[#F5F7FA] border-slate-200 h-11 font-semibold" />
            </div>
            {toEdit?.project?.id && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="space-y-1.5">
                  <Label>Bölge</Label>
                  <Select value={editZone} onValueChange={setEditZone}>
                    <SelectTrigger data-testid="edit-zone-select" className="rounded-none bg-[#F5F7FA] border-slate-200 h-11"><SelectValue placeholder="Bölge" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {["Blue Zone", "Green Zone", "Ofisler"].map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Stand No</Label>
                  <Input data-testid="edit-stand-input" value={editStand} onChange={(e) => setEditStand(e.target.value)} className="rounded-none bg-[#F5F7FA] border-slate-200 h-11" />
                </div>
                <div className="space-y-1.5">
                  <Label>Proje Türü</Label>
                  <Select value={editType} onValueChange={setEditType}>
                    <SelectTrigger data-testid="edit-type-select" className="rounded-none bg-[#F5F7FA] border-slate-200 h-11"><SelectValue placeholder="Tür" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {["Fuar Standı", "Etkinlik", "Teknik"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Alan (m²)</Label>
                  <Input data-testid="edit-area-input" type="number" min="0" value={editArea} onChange={(e) => setEditArea(e.target.value)} className="rounded-none bg-[#F5F7FA] border-slate-200 h-11" />
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Talep Metni</Label>
              <Textarea data-testid="edit-message-input" value={editMessage} onChange={(e) => setEditMessage(e.target.value)} rows={7} className="rounded-none bg-[#F5F7FA] border-slate-200 text-sm leading-relaxed" />
            </div>
            <div className="space-y-1.5">
              <Label>Son Teklif Tarihi ve Saati (Deadline)</Label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" data-testid="edit-deadline-trigger" className="flex-1 justify-start rounded-none bg-[#F5F7FA] border-slate-200 hover:bg-slate-100 h-11 font-normal gap-2">
                      <CalendarIcon size={15} className="text-slate-500" />
                      {editDeadline ? format(editDeadline, "dd MMMM yyyy", { locale: tr }) : <span className="text-slate-500">Tarih seçin</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 bg-white border-slate-200 rounded-none w-auto">
                    <Calendar mode="single" selected={editDeadline} onSelect={setEditDeadline} locale={tr} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} />
                  </PopoverContent>
                </Popover>
                <Select value={editTime} onValueChange={setEditTime}>
                  <SelectTrigger className="rounded-none bg-[#F5F7FA] border-slate-200 h-11 sm:w-32" data-testid="edit-time-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none max-h-64">
                    {Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`).map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={saveEdit} disabled={saving} data-testid="edit-save-button" className="rounded-none h-11 px-8 bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!toExtend} onOpenChange={(o) => { if (!o) setToExtend(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-lg" data-testid="extend-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
              <CalendarClock size={20} className="text-amber-600" /> İhale Süresini Uzat
            </DialogTitle>
          </DialogHeader>
          {toExtend && (
            <div className="space-y-5">
              <div className="border border-slate-200 bg-slate-50 p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Mevcut Son Tarih</div>
                  <div className="text-sm font-semibold mt-0.5" data-testid="extend-current-deadline">{deadlineLabel(toExtend)}</div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">{toExtend.title}</div>
                </div>
                {toExtend.expired ? (
                  <Badge variant="outline" className="rounded-none text-xs bg-red-500/10 text-red-500 border-red-500/20 shrink-0">İhale Sona Erdi</Badge>
                ) : (
                  <Badge variant="outline" className="rounded-none text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/20 shrink-0">Aktif</Badge>
                )}
              </div>
              {toExtend.expired && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="extend-reopen-note">
                  Süresi dolmuş bu ihale, uzatma onaylandığında otomatik olarak yeniden teklif almaya açılır.
                </p>
              )}
              <div className="space-y-1.5">
                <Label>Hızlı Seçim</Label>
                <div className="flex gap-2 flex-wrap">
                  {[{ l: "+1 Gün", d: 1 }, { l: "+3 Gün", d: 3 }, { l: "+1 Hafta", d: 7 }, { l: "+2 Hafta", d: 14 }].map((q) => (
                    <Button key={q.d} type="button" variant="outline" size="sm" onClick={() => quickPick(q.d)} data-testid={`quick-extend-${q.d}`} className="rounded-none border-slate-200 hover:bg-slate-100 h-8 text-xs font-medium">
                      {q.l}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Yeni Son Tarih ve Saat</Label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" data-testid="extend-date-trigger" className="flex-1 justify-start rounded-none bg-[#F5F7FA] border-slate-200 hover:bg-slate-100 h-11 font-normal gap-2">
                        <CalendarIcon size={15} className="text-slate-500" />
                        {extDate ? format(extDate, "dd MMMM yyyy", { locale: tr }) : <span className="text-slate-500">Tarih seçin</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 bg-white border-slate-200 rounded-none w-auto">
                      <Calendar mode="single" selected={extDate} onSelect={setExtDate} locale={tr} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} />
                    </PopoverContent>
                  </Popover>
                  <Select value={extTime} onValueChange={setExtTime}>
                    <SelectTrigger className="rounded-none bg-[#F5F7FA] border-slate-200 h-11 sm:w-32" data-testid="extend-time-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-none max-h-64">
                      {extTimeOpts.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                      {extTimeOpts.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Bugün için uygun saat kalmadı — ileri bir tarih seçin</div>}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-slate-500">Teklifler yeni gün ve saate kadar (Türkiye saati) verilebilir.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Uzatma Gerekçesi (opsiyonel)</Label>
                <Select value={extReasonPreset} onValueChange={setExtReasonPreset}>
                  <SelectTrigger className="rounded-none bg-[#F5F7FA] border-slate-200 h-11" data-testid="extend-reason-select">
                    <SelectValue placeholder="Hazır gerekçe seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none max-h-64">
                    <SelectItem value="none" data-testid="reason-option-none">Hazır gerekçe kullanma</SelectItem>
                    {[
                      "Tedarikçilerden gelen genel talep üzerine süre uzatılmıştır",
                      "Yeterli sayıda teklif alınamadığından süre uzatılmıştır",
                      "Proje kapsamında / şartnamede güncelleme yapılmıştır",
                      "Değerlendirme sürecine ek zaman tanınmıştır",
                      "Resmi tatil / hafta sonu nedeniyle süre uzatılmıştır",
                      "Teknik aksaklık nedeniyle süre uzatılmıştır",
                    ].map((o, i) => (
                      <SelectItem key={o} value={o} data-testid={`reason-option-${i + 1}`}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea value={extReason} onChange={(e) => setExtReason(e.target.value)} rows={3} placeholder="Ek açıklama (opsiyonel) — tedarikçilere gönderilecek e-postada yer alır..." data-testid="extend-reason-input" className="rounded-none bg-[#F5F7FA] border-slate-200" />
              </div>
              <div className="space-y-1.5">
                <Label>Ek Firma Davet Et (opsiyonel)</Label>
                <Input
                  value={extQ}
                  onChange={(e) => setExtQ(e.target.value)}
                  placeholder="Firma ara..."
                  data-testid="extend-supplier-search"
                  className="rounded-none h-9 bg-[#F5F7FA] border-slate-200"
                />
                <div className="space-y-1 max-h-40 overflow-y-auto border border-slate-200 p-1" data-testid="extend-supplier-list">
                  {extSuppliers.filter((s) => !extQ.trim() || trFold(s.company_name || "").includes(trFold(extQ))).map((s) => (
                    <label key={s.id} className="flex items-center gap-2.5 px-2 py-1.5 text-sm cursor-pointer hover:bg-slate-50 transition-colors duration-150" data-testid={`extend-supplier-item-${s.id}`}>
                      <input
                        type="checkbox"
                        checked={extSel.includes(s.id)}
                        onChange={(e) => setExtSel((xs) => (e.target.checked ? [...xs, s.id] : xs.filter((x) => x !== s.id)))}
                      />
                      <span className="font-medium">{s.company_name}</span>
                      {s._invitedNoBid && (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">Davetli · teklif vermedi</span>
                      )}
                      <span className="text-xs text-slate-400 ml-auto">{s.contact_name}</span>
                    </label>
                  ))}
                  {extSuppliers.length === 0 && <div className="text-xs text-slate-400 py-2 text-center">Davet edilebilecek ek firma yok</div>}
                </div>
                {extSel.length > 0 && (
                  <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 px-3 py-2" data-testid="extend-invite-note">
                    {extSel.length} firma davet e-postası alacak (daha önce davetli olup teklif vermeyenler dahil); yeni son tarihe kadar teklif verebilecekler.
                  </p>
                )}
              </div>
              {extDate && (
                <div className="border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm flex items-center gap-2 flex-wrap" data-testid="extend-summary">
                  <span className="text-slate-500 line-through">{deadlineLabel(toExtend)}</span>
                  <span className="text-slate-400">→</span>
                  <span className="font-semibold text-emerald-700">{format(extDate, "dd.MM.yyyy")} · {extTime}</span>
                </div>
              )}
              <p className="text-xs text-slate-500">Uzatma onaylandığında tüm davetli tedarikçilere ve proje sahibine e-posta ile bilgi verilir; işlem kaydı ihale geçmişinde saklanır.</p>
              <Button onClick={submitExtend} disabled={extending || !extDate} data-testid="extend-submit-button" className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
                <CalendarClock size={15} /> {extending ? "Uzatılıyor..." : "Süreyi Uzat ve Bildir"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent className="bg-white border-slate-200 rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Teklif talebini sil</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              <span className="text-slate-900 font-medium">{toDelete?.title}</span> talebini ve bu talebe gelen{" "}
              <span className="text-slate-900 font-medium">{toDelete?.offer_count ?? 0} teklifi</span> kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!hasPrivilege() ? (
            <div className="space-y-1.5 py-1">
              <Label className="flex items-center gap-1.5 text-amber-700"><ShieldCheck size={13} /> Süper Şifre</Label>
              <Input type="password" value={delPw} onChange={(e) => setDelPw(e.target.value)} placeholder="Silme işlemi süper şifre gerektirir" data-testid="delete-super-password-input" className="rounded-none border-amber-300 h-10 bg-amber-50/50" />
            </div>
          ) : (
            <p className="text-[11px] text-emerald-700 flex items-center gap-1.5 py-1" data-testid="delete-privilege-note"><ShieldCheck size={12} /> Özel Yetki aktif — süper şifre gerekmez.</p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-slate-200 hover:bg-slate-100" data-testid="delete-cancel-button" onClick={() => setDelPw("")}>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={!hasPrivilege() && !delPw} className="rounded-none bg-red-600 hover:bg-red-500 text-white disabled:opacity-50" data-testid="delete-confirm-button">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
